import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, MessageCircle, Power, PowerOff, KeyRound, Eye, EyeOff, RefreshCw, Copy, Check } from "lucide-react";
import { formatDate } from "@/lib/constants";

export default function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", whatsapp: "" });
  const [revealedIds, setRevealedIds] = useState({});
  const [copiedId, setCopiedId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/agents");
      setAgents(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/agents", { name: form.name.trim(), whatsapp: form.whatsapp.trim(), active: true });
      setForm({ name: "", whatsapp: "" });
      toast.success(`Agent ajouté · code d'accès : ${data.access_code}`, { duration: 8000 });
      // Reveal the newly created code by default so admin can copy it once
      setRevealedIds((p) => ({ ...p, [data.id]: true }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const toggle = async (a) => {
    try {
      await api.put(`/admin/agents/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Agent désactivé" : "Agent activé");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'agent ${a.name} ?`)) return;
    try {
      await api.delete(`/admin/agents/${a.id}`);
      toast.success("Agent supprimé");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const regenerateCode = async (a) => {
    if (!window.confirm(`Régénérer le code d'accès de ${a.name} ? L'ancien code ne fonctionnera plus.`)) return;
    try {
      const { data } = await api.post(`/admin/agents/${a.id}/regenerate-code`);
      toast.success(`Nouveau code : ${data.access_code}`, { duration: 8000 });
      setRevealedIds((p) => ({ ...p, [a.id]: true }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const copyCode = async (a) => {
    try {
      await navigator.clipboard.writeText(a.access_code || "");
      setCopiedId(a.id);
      toast.success(`Code de ${a.name} copié`);
      setTimeout(() => setCopiedId(""), 1500);
    } catch (_e) {
      toast.error("Impossible de copier");
    }
  };

  const toggleReveal = (id) => setRevealedIds((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold">Agents</h1>
      <p className="text-gray-600 mt-1">
        Les agents actifs reçoivent une notification WhatsApp à chaque nouvelle opération en attente. Chaque agent dispose d'un <span className="font-semibold">code d'accès à 6 chiffres</span> qu'il doit saisir pour consulter une transaction via le lien du client.
      </p>

      <form onSubmit={create} className="sl-card mt-6 max-w-2xl" data-testid="agent-form">
        <h3 className="font-heading font-semibold mb-4">Ajouter un agent</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="sl-label">Nom de l'agent</label>
            <input
              className="sl-input" required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex. Larry Owanga"
              data-testid="agent-name"
            />
          </div>
          <div>
            <label className="sl-label">Numéro WhatsApp (avec indicatif)</label>
            <input
              className="sl-input font-mono" required
              value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              placeholder="+24177861180"
              data-testid="agent-whatsapp"
            />
          </div>
        </div>
        <button type="submit" disabled={creating} className="sl-btn-primary mt-4" data-testid="agent-create">
          <Plus size={16} /> {creating ? "Ajout…" : "Ajouter l'agent"}
        </button>
        <p className="text-xs text-gray-500 mt-3">
          Un code d'accès à 6 chiffres sera généré automatiquement à la création. Communiquez-le à l'agent en personne ou par message privé sécurisé.
        </p>
      </form>

      <div className="sl-card mt-8 p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Chargement…</div>
        ) : agents.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            Aucun agent enregistré. Ajoutez-en un ci-dessus pour qu'il reçoive les alertes WhatsApp.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="agents-table">
            <thead className="bg-[var(--sl-bg-alt)] text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left p-4">Nom</th>
                <th className="text-left p-4">WhatsApp</th>
                <th className="text-left p-4">Code d'accès</th>
                <th className="text-left p-4">Statut</th>
                <th className="text-left p-4">Créé</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => {
                const revealed = !!revealedIds[a.id];
                const code = a.access_code || "—";
                return (
                  <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="p-4 font-medium">
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 rounded-full grid place-items-center" style={{ background: "#25D366" }}>
                          <MessageCircle size={14} className="text-white" />
                        </span>
                        {a.name}
                      </div>
                    </td>
                    <td className="p-4 font-mono">{a.whatsapp}</td>
                    <td className="p-4">
                      <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5">
                        <KeyRound size={14} className="text-[var(--sl-primary)]" />
                        <span className="font-mono font-bold tracking-widest" data-testid={`agent-code-${a.id}`}>
                          {revealed ? code : "••••••"}
                        </span>
                        <button
                          onClick={() => toggleReveal(a.id)}
                          className="text-gray-400 hover:text-gray-700"
                          title={revealed ? "Masquer" : "Afficher"}
                          data-testid={`agent-toggle-reveal-${a.id}`}
                        >
                          {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          onClick={() => copyCode(a)}
                          className="text-gray-400 hover:text-[var(--sl-primary)]"
                          title="Copier le code"
                          data-testid={`agent-copy-code-${a.id}`}
                        >
                          {copiedId === a.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                        </button>
                        <button
                          onClick={() => regenerateCode(a)}
                          className="text-gray-400 hover:text-amber-600"
                          title="Régénérer un nouveau code"
                          data-testid={`agent-regenerate-code-${a.id}`}
                        >
                          <RefreshCw size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={a.active ? "sl-badge sl-badge-completed" : "sl-badge sl-badge-cancelled"} data-testid={`agent-status-${a.id}`}>
                        {a.active ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="p-4 text-gray-500">{formatDate(a.created_at)}</td>
                    <td className="p-4 text-right space-x-2">
                      <button
                        onClick={() => toggle(a)}
                        className="text-sm text-gray-600 hover:text-[var(--sl-primary)] inline-flex items-center gap-1"
                        data-testid={`agent-toggle-${a.id}`}
                      >
                        {a.active ? <><PowerOff size={14} /> Désactiver</> : <><Power size={14} /> Activer</>}
                      </button>
                      <button
                        onClick={() => remove(a)}
                        className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1 ml-3"
                        data-testid={`agent-delete-${a.id}`}
                      >
                        <Trash2 size={14} /> Supprimer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
